'use client'

import React, { useEffect } from 'react'
import SimpleBlockContent from '../../SimpleBlockContent'
import HighlightedText from '../../Utils/HighlightedText'
import CtaV2 from '../../CtaV2'
import { useRegionSubscriptions } from '../../RegionSurfaces'

// Fallback images (local files)
const fallbackImages = {
  hero: '/images/team-vrs-nascar/hero-pocono.png',
  coach: '/images/team-vrs-nascar/coach-bobby-zalenski-new.png',
  whyWeWin: '/images/team-vrs-nascar/racing-talladega-wins.png',
}

// Helper to get image source (resolved descriptor or local fallback)
// task #67: content images are now ResolvedImage descriptors; use .src
const getImgSrc = (image, width, fallback) => {
  if (image && image.src) {
    return image.src
  }
  return fallback
}

// Colors
const colors = {
  bgDark: '#000000',
  bgLight: '#ffffff',
  textPrimary: '#f4f4f4',
  textPrimaryLight: '#161616',
  textTertiary: '#6f6f6f',
  textTertiaryDark: '#8d8d8d',
  green: '#42be65',
}

// Feature Pictogram Icons (matching Figma design, 32x32)
const EventIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16 2L20 10H12L16 2Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
    <path d="M16 10V16" stroke="currentColor" strokeWidth="1.5"/>
    <rect x="8" y="16" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
    <path d="M12 12V16" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M20 12V16" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M8 22H24" stroke="currentColor" strokeWidth="1.5"/>
  </svg>
)

// Filled checkmark icon (light gray circle with checkmark, 24x24)
const CheckIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 1.5C9.9233 1.5 7.89323 2.11581 6.16652 3.26957C4.4398 4.42332 3.09399 6.0632 2.29927 7.98182C1.50455 9.90045 1.29661 12.0116 1.70176 14.0484C2.1069 16.0852 3.10693 17.9562 4.57538 19.4246C6.04383 20.8931 7.91476 21.8931 9.95156 22.2982C11.9884 22.7034 14.0996 22.4955 16.0182 21.7007C17.9368 20.906 19.5767 19.5602 20.7304 17.8335C21.8842 16.1068 22.5 14.0767 22.5 12C22.5 9.21523 21.3938 6.54451 19.4246 4.57538C17.4555 2.60625 14.7848 1.5 12 1.5ZM10.5 16.1931L6.75 12.4431L7.94296 11.25L10.5 13.8069L16.0575 8.25L17.2543 9.43943L10.5 16.1931Z" fill="#F4F4F4"/>
  </svg>
)


// Grid decoration for features section (5x5 grid of rounded squares, 540x540 per Figma)
const GridDecoration = () => (
  <div className="relative w-[300px] h-[300px] md:w-[400px] md:h-[400px] lg:w-[540px] lg:h-[540px]">
    <svg width="100%" height="100%" viewBox="0 0 540 540" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="gridGradient" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
          <stop offset="0%" stopColor="#6f6f6f" stopOpacity="0.6"/>
          <stop offset="100%" stopColor="#393939" stopOpacity="0.1"/>
        </radialGradient>
      </defs>
      {/* 5x5 grid of rounded squares (540/5 = 108 per cell) */}
      {[0, 1, 2, 3, 4].flatMap(row =>
        [0, 1, 2, 3, 4].map(col => {
          const x = col * 108 + 9
          const y = row * 108 + 9
          const distance = Math.sqrt(Math.pow(col - 2, 2) + Math.pow(row - 2, 2))
          const opacity = Math.max(0.15, 0.7 - distance * 0.15)
          return (
            <rect
              key={`grid-${row}-${col}`}
              x={x}
              y={y}
              width="90"
              height="90"
              rx="16"
              stroke="#8d8d8d"
              strokeWidth="1"
              fill="none"
              opacity={opacity}
            />
          )
        })
      )}
    </svg>
  </div>
)

// Format price from cents to display string
const formatPrice = (priceCents, currency = 'USD') => {
  if (!priceCents) return null
  const price = priceCents / 100
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
  }).format(price)
}

function TeamVrsNascarPage(props) {
  const {
    // Hero
    heroImage,
    heroTitle,
    heroSubtitle,
    heroCta,
    // Features
    valuePropsEyebrow,
    valuePropsTitle,
    valuePropsDescription,
    valuePropsCenterImage,
    valueProps,
    // Coach
    coachImage,
    coachEyebrow,
    coachTitle,
    coachBio,
    coachCredentials,
    // Why We Win
    whyWeWinEyebrow,
    whyWeWinTitle,
    whyWeWinDescription,
    whyWeWinCta,
    whyWeWinImage,
    // Steps
    stepsTitle,
    stepsDescription,
    steps,
    // Pricing (fallback from CMS)
    priceAmount,
    pricePeriod,
    ctaTitle,
    ctaSubtitle,
    ctaCta,
  } = props

  // Overmind→server-props (Phase A2.2): subscriptions come from the SSR-threaded
  // <RegionSurfacesProvider> (byte-identical to the store, which stays hydrated),
  // with an Overmind fallback for any non-page entry point.
  const { subscriptions, ensureRequested } = useRegionSubscriptions()

  useEffect(() => {
    ensureRequested()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Find the NASCAR Team VRS subscription
  const nascarSubscription = subscriptions.find(s => s.code === 'nascarTeamVrs')

  // Get price from backend or fall back to CMS
  const displayPrice = nascarSubscription?.price?.monthly?.priceCents
    ? formatPrice(nascarSubscription.price.monthly.priceCents, nascarSubscription.price.monthly.currency)
    : priceAmount
  const displayPeriod = nascarSubscription?.price?.monthly ? 'mo' : pricePeriod

  return (
    <div className="flex flex-col items-center w-full overflow-x-hidden" style={{ background: colors.bgDark, paddingLeft: '24px', paddingRight: '24px', boxSizing: 'border-box' }}>
      {/* ============================================ */}
      {/* HERO SECTION */}
      {/* ============================================ */}
      <section className="flex flex-col items-center w-full px-6 py-16 md:px-8 md:py-20 lg:px-16 lg:py-28">
        <div className="flex flex-col items-center gap-12 md:gap-20 max-w-[1280px] w-full">
          {/* Hero Content */}
          <div className="flex flex-col items-center gap-6 max-w-[768px] w-full text-center">
            {heroTitle && (
              // Single-<h1> rule (Audit A #1): /nascar-team-vrs hero title is the
              // page's one visible <h1>. `.h1-contents` adds no box (byte-identical);
              // the section's other titles stay <h2>/<h3>.
              <h1 className="h1-contents">
                <HighlightedText
                  text={heroTitle.text}
                  textColor={heroTitle.textColor}
                  highlight={heroTitle.highlight}
                  highlightColor={heroTitle.highlightColor}
                  highlightExactMatch
                />
              </h1>
            )}
            {heroSubtitle && (
              <HighlightedText
                text={heroSubtitle.text}
                textColor={heroSubtitle.textColor}
                highlight={heroSubtitle.highlight}
                highlightColor={heroSubtitle.highlightColor}
                highlightExactMatch
              />
            )}
            {heroCta && <CtaV2 {...heroCta} light />}
          </div>

          {/* Hero Image */}
          {(heroImage || fallbackImages.hero) && (
            <div className="relative w-full aspect-[1280/640] rounded-2xl overflow-hidden">
              {/* eslint-disable-next-line local-rules/prefer-img-component -- TODO(#67-img): content image already sized (1280x640) via getImgSrc; migrating to <Img> needs the resolver + object-cover/h-[130%] layout reworked (non-trivial) */}
              <img
                src={getImgSrc(heroImage, 1280, fallbackImages.hero)}
                alt="NASCAR racing"
                className="w-full h-[130%] object-cover object-top"
                width={1280}
                height={640}
              />
              {/* Grid overlay */}
              <div className="absolute inset-0 pointer-events-none">
                {/* eslint-disable-next-line local-rules/no-raw-media-src, local-rules/prefer-img-component -- TODO(#67): decorative static-public grid-overlay SVG (Next /public), hand-sized; not resolver-managed content media */}
                <img src="/images/team-vrs-nascar/hero-grid-overlay.svg"
                  alt=""
                  className="w-full h-full"
                  width={1280}
                  height={640}
                />
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ============================================ */}
      {/* FEATURES SECTION */}
      {/* ============================================ */}
      <section className="flex flex-col items-center w-full px-6 py-16 md:px-8 md:py-20 lg:px-16 lg:py-28">
        <div className="flex flex-col items-center gap-12 md:gap-20 max-w-[1280px] w-full">
          {/* Section Header */}
          <div className="flex flex-col items-center gap-4 max-w-[768px] w-full text-center">
            {valuePropsEyebrow && (
              <p className="font-kanit font-semibold text-base" style={{ color: colors.textTertiary }}>
                {valuePropsEyebrow}
              </p>
            )}
            {valuePropsTitle && (
              <h2 className="font-kanit font-semibold text-2xl md:text-4xl lg:text-5xl leading-tight md:leading-[42px] lg:leading-[56px]" style={{ color: colors.textPrimary }}>
                <SimpleBlockContent
                  blocks={valuePropsTitle.text}
                  highlight={valuePropsTitle.highlight}
                  highlightSx={`color: ${colors.textTertiaryDark};`}
                  highlightExactMatch
                />
              </h2>
            )}
            {valuePropsDescription && (
              <p className="font-kanit font-light text-base md:text-lg leading-6" style={{ color: colors.textTertiary }}>
                {valuePropsDescription}
              </p>
            )}
          </div>

          {/* Features Grid */}
          <div className="flex flex-col lg:flex-row items-center gap-12 w-full">
            {/* Left Column */}
            <div className="flex-1 flex flex-col items-center gap-12 md:gap-16 w-full lg:w-auto">
              {valueProps?.slice(0, 2).map((feature, index) => {
                return (
                  <div key={index} className="flex flex-col items-center gap-6 text-center max-w-[320px]">
                    <div className="w-16 h-16">
                      {/* task #67: icon is a ResolvedImage descriptor */}
                      {feature.icon && feature.icon.src ? (
                        /* eslint-disable-next-line local-rules/prefer-img-component -- resolved-descriptor content icon, hand-sized 64x64; a full <Img> box is unnecessary at this size */
                        <img
                          src={feature.icon.src}
                          alt=""
                          className="w-full h-full object-contain"
                          width={64}
                          height={64}
                        />
                      ) : (
                        <EventIcon />
                      )}
                    </div>
                    <h3 className="font-kanit font-semibold text-xl md:text-2xl leading-7" style={{ color: colors.textPrimary }}>
                      {feature.title}
                    </h3>
                    <p className="font-kanit font-light text-base leading-6" style={{ color: colors.textTertiary }}>
                      {feature.description}
                    </p>
                    {feature.footnote && (
                      <p className="font-kanit font-light text-sm leading-5 -mt-4" style={{ color: colors.textTertiary }}>
                        {feature.footnote}
                      </p>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Center Grid Decoration */}
            <div className="order-first lg:order-none flex-shrink-0">
              {/* task #67: center image is a ResolvedImage descriptor */}
              {valuePropsCenterImage && valuePropsCenterImage.src ? (
                /* eslint-disable-next-line local-rules/prefer-img-component -- TODO(#67-img): resolved-descriptor content image already sized (540x540); responsive object-contain layout, migrate to <Img> in a later image-framework pass */
                <img
                  src={valuePropsCenterImage.src}
                  alt=""
                  className="w-[300px] h-[300px] md:w-[400px] md:h-[400px] lg:w-[540px] lg:h-[540px] object-contain"
                  width={540}
                  height={540}
                />
              ) : (
                <GridDecoration />
              )}
            </div>

            {/* Right Column */}
            <div className="flex-1 flex flex-col items-center gap-12 md:gap-16 w-full lg:w-auto">
              {valueProps?.slice(2, 4).map((feature, index) => {
                return (
                  <div key={index} className="flex flex-col items-center gap-6 text-center max-w-[320px]">
                    <div className="w-16 h-16">
                      {/* task #67: icon is a ResolvedImage descriptor */}
                      {feature.icon && feature.icon.src ? (
                        /* eslint-disable-next-line local-rules/prefer-img-component -- resolved-descriptor content icon, hand-sized 64x64; a full <Img> box is unnecessary at this size */
                        <img
                          src={feature.icon.src}
                          alt=""
                          className="w-full h-full object-contain"
                          width={64}
                          height={64}
                        />
                      ) : (
                        <EventIcon />
                      )}
                    </div>
                    <h3 className="font-kanit font-semibold text-xl md:text-2xl leading-7" style={{ color: colors.textPrimary }}>
                      {feature.title}
                    </h3>
                    <p className="font-kanit font-light text-base leading-6" style={{ color: colors.textTertiary }}>
                      {feature.description}
                    </p>
                    {feature.footnote && (
                      <p className="font-kanit font-light text-sm leading-5 -mt-4" style={{ color: colors.textTertiary }}>
                        {feature.footnote}
                      </p>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* COACH SECTION (Dark Background) */}
      {/* ============================================ */}
      <section className="flex flex-col items-center w-full px-6 py-16 md:px-8 md:py-20 lg:px-16 lg:py-28 overflow-hidden" style={{ background: colors.bgDark }}>
        <div className="flex flex-col lg:flex-row items-center gap-12 md:gap-20 max-w-[1280px] w-full">
          {/* Coach Image - Circular per Figma */}
          {(coachImage || fallbackImages.coach) && (
            <div className="w-full max-w-[300px] md:max-w-[400px] lg:max-w-[500px] flex-shrink-0 mx-auto lg:mx-0">
              <div className="aspect-square rounded-full border border-white/[0.1] overflow-hidden">
                {/* eslint-disable-next-line local-rules/prefer-img-component -- TODO(#67-img): content image already sized (600x600) via getImgSrc; migrating to <Img> needs the resolver + circular object-cover layout reworked (non-trivial) */}
                <img
                  src={getImgSrc(coachImage, 600, fallbackImages.coach)}
                  alt="Bobby Zalenski - Team VRS NASCAR Coach"
                  className="w-full h-full object-cover object-top"
                  width={600}
                  height={600}
                />
              </div>
            </div>
          )}

          {/* Coach Content */}
          <div className="flex-1 flex flex-col gap-6 text-center lg:text-left">
            <div className="flex flex-col gap-2">
              {coachEyebrow && (
                <p className="font-kanit font-semibold text-base" style={{ color: colors.textTertiary }}>
                  {coachEyebrow}
                </p>
              )}
              {coachTitle && (
                <h2 className="font-kanit font-semibold text-2xl md:text-4xl lg:text-5xl leading-tight md:leading-[42px] lg:leading-[56px]" style={{ color: colors.textPrimary }}>
                  <SimpleBlockContent
                    blocks={coachTitle.text}
                    highlight={coachTitle.highlight}
                    highlightSx={`color: ${colors.textTertiary};`}
                    highlightExactMatch
                  />
                </h2>
              )}
            </div>

            {coachBio && (
              <div className="font-kanit font-light text-base md:text-lg leading-7" style={{ color: colors.textTertiary }}>
                <SimpleBlockContent
                  blocks={coachBio.text}
                  highlight={coachBio.highlight}
                  highlightSx={`color: ${colors.textPrimary};`}
                  highlightExactMatch
                />
              </div>
            )}

            {/* Credentials List - 16px gap per Figma */}
            {coachCredentials && coachCredentials.length > 0 && (
              <ul className="flex flex-col gap-4 items-center lg:items-start pl-0 list-none">
                {coachCredentials.map((credential, index) => (
                  <li key={index} className="flex items-center gap-4 text-left">
                    <span className="flex-shrink-0">
                      <CheckIcon />
                    </span>
                    <span className="font-kanit font-light text-base leading-6" style={{ color: colors.textPrimary }}>
                      <strong className="font-normal">{credential.title}</strong>{' '}
                      <span style={{ color: colors.textTertiaryDark }}>{credential.description}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* WHY OUR TEAM WINS SECTION (Dark Background) */}
      {/* ============================================ */}
      <section className="flex flex-col items-center w-full px-6 py-16 md:px-8 md:py-20 lg:px-16 lg:py-28 overflow-hidden" style={{ background: colors.bgDark }}>
        <div className="flex flex-col lg:flex-row items-center lg:items-start gap-12 lg:gap-16 max-w-[1280px] w-full">
          {/* Content */}
          <div className="flex-1 flex flex-col gap-8 text-center lg:text-left">
            <div className="flex flex-col gap-4">
              {whyWeWinEyebrow && (
                <p className="font-kanit font-semibold text-base" style={{ color: colors.textTertiary }}>
                  {whyWeWinEyebrow}
                </p>
              )}
              {whyWeWinTitle && (
                <h2 className="font-kanit font-semibold text-2xl md:text-4xl lg:text-[48px] leading-tight md:leading-[42px] lg:leading-[56px]" style={{ color: colors.textPrimary }}>
                  <SimpleBlockContent
                    blocks={whyWeWinTitle.text}
                    highlight={whyWeWinTitle.highlight}
                    highlightSx={`color: ${colors.textTertiaryDark};`}
                    highlightExactMatch
                  />
                </h2>
              )}
            </div>
            <div className="flex flex-col gap-8">
              {whyWeWinDescription && (
                <p className="font-kanit font-light text-base md:text-lg leading-6" style={{ color: colors.textTertiary }}>
                  {whyWeWinDescription}
                </p>
              )}
              {whyWeWinCta && <div className="flex justify-center lg:justify-start"><CtaV2 {...whyWeWinCta} light /></div>}
            </div>
          </div>

          {/* Image - 608x608 with grid overlay per Figma */}
          {(whyWeWinImage || fallbackImages.whyWeWin) && (
            <div className="w-full max-w-[400px] lg:max-w-[500px] flex-shrink-0 mx-auto lg:mx-0">
              <div className="relative aspect-square rounded-[32px] overflow-hidden border border-white/[0.1]">
                {/* eslint-disable-next-line local-rules/prefer-img-component -- TODO(#67-img): content image already sized (608x608) via getImgSrc; migrating to <Img> needs the resolver + object-cover layout reworked (non-trivial) */}
                <img
                  src={getImgSrc(whyWeWinImage, 608, fallbackImages.whyWeWin)}
                  alt="NASCAR racing at Talladega"
                  className="w-full h-full object-cover"
                  width={608}
                  height={608}
                />
                {/* Grid overlay from Figma */}
                <div className="absolute inset-0 pointer-events-none">
                  {/* eslint-disable-next-line local-rules/no-raw-media-src, local-rules/prefer-img-component -- TODO(#67): decorative static-public grid-overlay SVG (Next /public), hand-sized; not resolver-managed content media */}
                  <img src="/images/team-vrs-nascar/why-we-win-grid-overlay.svg"
                    alt=""
                    className="w-full h-full"
                    width={609}
                    height={609}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ============================================ */}
      {/* ENROLL IN 3 STEPS SECTION (Dark Background) */}
      {/* ============================================ */}
      <section className="flex flex-col items-center w-full px-6 py-16 md:px-8 md:py-20 lg:px-16 lg:py-28">
        <div className="flex flex-col gap-12 lg:gap-20 max-w-[1280px] w-full">
          {/* Header Row - 80px gap per Figma */}
          <div className="flex flex-col lg:flex-row lg:items-start gap-6 lg:gap-20">
            {stepsTitle && (
              <h2 className="flex-1 font-kanit font-semibold text-2xl md:text-4xl lg:text-[48px] leading-tight md:leading-[42px] lg:leading-[56px] text-center lg:text-left" style={{ color: colors.textTertiaryDark }}>
                <SimpleBlockContent
                  blocks={stepsTitle.text}
                  highlight={stepsTitle.highlight}
                  highlightSx={`color: ${colors.textPrimary};`}
                  highlightExactMatch
                />
              </h2>
            )}
            {stepsDescription && (
              <p className="flex-1 font-kanit font-light text-base md:text-lg leading-6 text-center lg:text-left" style={{ color: colors.textTertiary }}>
                {stepsDescription}
              </p>
            )}
          </div>

          {/* Steps Grid - 48px gap per Figma */}
          {steps && steps.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
              {steps.map((step, index) => (
                <div key={index} className="flex flex-col gap-6 text-center md:text-left">
                  {/* Step Number - 80px Doto SemiBold */}
                  <p className="font-doto font-semibold text-6xl md:text-7xl lg:text-[80px] leading-none m-0 p-0" style={{ color: colors.textTertiary }}>
                    {String(index + 1).padStart(2, '0')}
                  </p>
                  {/* Step Title - 20px with mixed colors */}
                  {step.title && (
                    <h3 className="font-kanit font-semibold text-lg md:text-xl leading-7 m-0 p-0" style={{ color: colors.textTertiary }}>
                      <SimpleBlockContent
                        blocks={step.title.text}
                        highlight={step.title.highlight}
                        highlightSx={`color: ${colors.textPrimary};`}
                        highlightExactMatch
                      />
                    </h3>
                  )}
                  {/* Step Description */}
                  {step.description && (
                    <p className="font-kanit font-light text-base leading-6 m-0 p-0" style={{ color: colors.textTertiary }}>
                      {step.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ============================================ */}
      {/* PRICING CTA SECTION (Dark Background) */}
      {/* ============================================ */}
      <section id="pricing" className="flex flex-col items-center w-full px-6 py-16 md:px-8 md:py-20 lg:px-16 lg:py-28">
        <div className="flex flex-col items-center gap-12 max-w-[768px] w-full text-center">
          {/* Price - 48px amount, 32px period */}
          {displayPrice && (
            <p className="font-kanit font-semibold">
              <span className="text-4xl md:text-[48px] leading-[56px]" style={{ color: colors.textPrimary }}>
                {displayPrice}
              </span>
              {displayPeriod && (
                <span className="text-2xl md:text-[32px] leading-[40px]" style={{ color: colors.textTertiaryDark }}>
                  /{displayPeriod}
                </span>
              )}
            </p>
          )}

          {/* Title + Subtitle with 16px gap between them */}
          <div className="flex flex-col gap-4 w-full">
            {/* Title - 32px per Figma */}
            {ctaTitle && (
              <h2 className="font-kanit font-semibold text-2xl md:text-[32px] leading-tight md:leading-[40px]" style={{ color: colors.textPrimary }}>
                <SimpleBlockContent
                  blocks={ctaTitle.text}
                  highlight={ctaTitle.highlight}
                  highlightSx={`color: ${colors.textTertiaryDark};`}
                  highlightExactMatch
                />
              </h2>
            )}

            {/* Subtitle - 18px */}
            {ctaSubtitle && (
              <div className="font-kanit font-light text-base md:text-lg leading-6" style={{ color: colors.textTertiary }}>
                <SimpleBlockContent
                  blocks={ctaSubtitle.text}
                  highlight={ctaSubtitle.highlight}
                  highlightSx={`color: ${colors.textPrimary};`}
                  highlightExactMatch
                />
              </div>
            )}
          </div>

          {/* CTA Button */}
          {ctaCta && <CtaV2 {...ctaCta} light />}
        </div>
      </section>
    </div>
  )
}

export default TeamVrsNascarPage
